/**
 * Staff face enrolment with active liveness.
 *
 * Capture-only by operator decision (2026-08-25): no notice copy and no acknowledgement. The
 * electronic-monitoring disclosure therefore rides on the policy document HR distributes.
 * `privacy.requireAcknowledgement` restores the in-app notice if that changes.
 *
 * The camera is opened only when the employee presses Begin, so the browser permission prompt
 * is tied to a deliberate action rather than firing on page load.
 */

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import { CHALLENGE_PROMPTS, type ChallengeKind } from '../core/index.js';
import { enrolFace } from '../cv/identityClient';
import { useLivenessEnrolment, type CapturedFrame } from '../hooks/useLivenessEnrolment';

interface FaceEnrolmentPageProps {
  /** Server-issued challenge order. Omit only for local testing. */
  serverChallenges?: readonly ChallengeKind[];
  /** Called with the captured frames once liveness passes. */
  onEnrolled?: (frames: readonly CapturedFrame[]) => void;
  displayName?: string;
}

type SaveState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; sampleCount: number; minPairwiseSimilarity: number | null; rejected: string[] }
  | { kind: 'error'; message: string };

export function FaceEnrolmentPage({
  serverChallenges,
  onEnrolled,
  displayName,
}: FaceEnrolmentPageProps) {
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const enrolment = useLivenessEnrolment({
    ...(serverChallenges === undefined ? {} : { serverChallenges }),
    onComplete: (frames) => {
      void (async () => {
        setSave({ kind: 'saving' });
        try {
          const result = await enrolFace(frames.map((frame) => frame.blob));
          setSave({
            kind: 'saved',
            sampleCount: result.sampleCount,
            minPairwiseSimilarity: result.minPairwiseSimilarity,
            rejected: result.rejected,
          });
          // Notified only after the template is actually stored. Firing this alongside the
          // upload meant the gate re-checked enrolment while the request was still in flight,
          // saw "not enrolled", and left the operator stranded on a screen that had already
          // reported success.
          onEnrolled?.(frames);
        } catch (error) {
          setSave({
            kind: 'error',
            message: error instanceof Error ? error.message : String(error),
          });
        }
      })();
    },
  });

  const { phase, status, videoRef, challenges, policy, errorMessage, frames } = enrolment;
  const mirrored = policy.presentation.mirrorDisplay;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Box sx={{ maxWidth: 760, mx: 'auto', px: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
              Account setup
            </Typography>
            <Typography variant="h5" sx={{ mt: 0.5 }}>
              Set up face verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {displayName ? `${displayName} — ` : ''}
              follow the on-screen prompts so we can confirm a live person is present.
            </Typography>
          </Box>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                {/* Challenge checklist — order is fixed for this attempt and shown up front so
                    the employee knows what is coming, which reduces failed attempts. */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {challenges.map((kind, index) => {
                    const done = (status?.challengeIndex ?? 0) > index || phase === 'passed';
                    const current = (status?.challengeIndex ?? 0) === index && phase === 'running';
                    return (
                      <Chip
                        key={`${kind}-${index}`}
                        label={`${index + 1}. ${CHALLENGE_PROMPTS[kind].title}`}
                        size="small"
                        color={done ? 'success' : current ? 'primary' : 'default'}
                        variant={done || current ? 'filled' : 'outlined'}
                      />
                    );
                  })}
                </Stack>

                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: 460,
                    mx: 'auto',
                    aspectRatio: '4 / 3',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#0f1419',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    component="video"
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    aria-label="Live camera preview for face verification"
                    sx={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transform: mirrored ? 'scaleX(-1)' : 'none',
                    }}
                  />

                  {phase === 'idle' || phase === 'starting' ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(15, 20, 25, 0.82)',
                        color: 'common.white',
                        textAlign: 'center',
                        p: 2,
                      }}
                    >
                      {phase === 'starting' ? (
                        <Box>
                          <CircularProgress size={26} sx={{ mb: 1, color: 'common.white' }} />
                          <Typography variant="body2">Starting camera…</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          The camera stays off until you press Begin.
                        </Typography>
                      )}
                    </Box>
                  ) : null}
                </Box>

                {phase === 'running' && status !== null ? (
                  <Box>
                    <Typography variant="h6" align="center">
                      {status.prompt?.title ?? 'Follow the prompt'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ mt: 0.5 }}
                    >
                      {status.message}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      // While holding, show hold progress; otherwise show how close the pose is.
                      value={Math.round(
                        (status.phase === 'holding' ? status.holdProgress : status.progress) * 100,
                      )}
                      sx={{ mt: 1.5, height: 8, borderRadius: 4 }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      align="center"
                      sx={{ display: 'block', mt: 0.75 }}
                    >
                      Step {status.challengeIndex + 1} of {status.challengeCount}
                    </Typography>
                  </Box>
                ) : null}

                {phase === 'passed' ? (
                  <Stack spacing={1}>
                    <Alert severity="success" variant="outlined">
                      Liveness confirmed — {frames.length} reference image
                      {frames.length === 1 ? '' : 's'} captured.
                    </Alert>
                    {save.kind === 'saving' ? (
                      <Alert severity="info" variant="outlined">
                        Creating your verification reference…
                      </Alert>
                    ) : null}
                    {save.kind === 'saved' ? (
                      <Alert severity="success" variant="outlined">
                        Face verification is set up from {save.sampleCount} image
                        {save.sampleCount === 1 ? '' : 's'}.
                        {/* Enrolment frames that disagree produce a template which mis-verifies
                            its own owner, so the employee is told to redo it — without the raw
                            figure, which is meaningless without the scale and is recorded in the
                            template row for anyone diagnosing it. */}
                        {save.minPairwiseSimilarity !== null &&
                        save.minPairwiseSimilarity < 0.5 ? (
                          <>
                            {' '}
                            The captured images did not agree closely with each other. Re-running
                            this in even, front-on lighting will give more reliable checks.
                          </>
                        ) : null}
                        {save.rejected.length > 0 ? (
                          <> {save.rejected.length} frame(s) were unusable and were skipped.</>
                        ) : null}
                      </Alert>
                    ) : null}
                    {save.kind === 'error' ? (
                      <Alert severity="error" variant="outlined">
                        {save.message}
                      </Alert>
                    ) : null}
                  </Stack>
                ) : null}

                {phase === 'failed' ? (
                  <Alert severity="warning" variant="outlined">
                    {errorMessage ?? status?.failureReason ?? 'Verification did not complete.'}
                  </Alert>
                ) : null}

                <Stack direction="row" spacing={1} justifyContent="center">
                  {phase === 'idle' ? (
                    <Button variant="contained" onClick={enrolment.start}>
                      Begin
                    </Button>
                  ) : null}
                  {phase === 'failed' ? (
                    <Button variant="contained" onClick={enrolment.retry}>
                      Try again
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Typography variant="caption" color="text.secondary">
            Images are captured in this browser and sent once to create your verification
            reference. They are not stored on this device.
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}
